'use client';

import { useInstantSearch, usePagination } from 'react-instantsearch';
import { SEARCH_HITS_PER_PAGE } from '@/constants/search.constants';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import { useCallback, useMemo } from 'react';
// Removed manual URL manipulation; pagination will sync via InstantSearch router
import DefaultButton from '@/components/common/default/DefaultButton';

// Constants matching the router logic
const MAX_VISIBLE_PAGES = 7;
const ELLIPSIS_THRESHOLD = 4;

// Types for better type safety
type PageItem = number | 'ellipsis-start' | 'ellipsis-end';

export default function CustomPagination() {
  const { refine, canRefine, currentRefinement, nbHits, nbPages, pages, isFirstPage, isLastPage } =
    usePagination();
  const { indexUiState } = useInstantSearch();
  const hitsPerPage = indexUiState.configure?.hitsPerPage ?? SEARCH_HITS_PER_PAGE;

  // No local derived state needed beyond visible pages

  // Enhanced page change handler: accept 0-based page index, clamp, scroll, and refine
  const handlePageChange = useCallback(
    (pageIndex: number) => {
      try {
        // Smooth scroll to top
        requestAnimationFrame(() => {
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        });

        const clamped = Math.max(0, Math.min(pageIndex, nbPages - 1));
        refine(clamped);
      } catch (error) {
        console.error('Error changing page:', error);
        const fallbackPageIndex = Math.max(0, Math.min(pageIndex, nbPages - 1));
        refine(fallbackPageIndex);
      }
    },
    [refine, nbPages]
  );

  // Enhanced function to determine which pages to show with better logic
  const getVisiblePages = useCallback((): PageItem[] => {
    // For small number of pages, show all
    if (nbPages <= MAX_VISIBLE_PAGES) {
      return pages;
    }

    const visiblePages: PageItem[] = [];
    const current = currentRefinement;
    const total = nbPages - 1; // Convert to 0-indexed for calculations

    // Always show first page
    visiblePages.push(0);

    // Determine middle section based on current position
    if (current < ELLIPSIS_THRESHOLD) {
      // Near start: show first few pages
      for (let i = 1; i <= Math.min(ELLIPSIS_THRESHOLD, total - 1); i++) {
        visiblePages.push(i);
      }
      if (total > ELLIPSIS_THRESHOLD) {
        visiblePages.push('ellipsis-end');
      }
    } else if (current > total - ELLIPSIS_THRESHOLD) {
      // Near end: show last few pages
      if (total > ELLIPSIS_THRESHOLD) {
        visiblePages.push('ellipsis-start');
      }
      for (let i = Math.max(1, total - ELLIPSIS_THRESHOLD); i < total; i++) {
        visiblePages.push(i);
      }
    } else {
      // Middle: show pages around current
      visiblePages.push('ellipsis-start');
      for (let i = current - 1; i <= current + 1; i++) {
        if (i > 0 && i < total) {
          visiblePages.push(i);
        }
      }
      visiblePages.push('ellipsis-end');
    }

    // Always show last page if it's not already included
    if (total > 0 && !visiblePages.includes(total)) {
      visiblePages.push(total);
    }

    return visiblePages;
  }, [nbPages, pages, currentRefinement]);

  // Memoize visible pages for performance
  const visiblePages = useMemo(() => getVisiblePages(), [getVisiblePages]);

  // Enhanced keyboard navigation handler
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, page: number) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handlePageChange(page);
      }
    },
    [handlePageChange]
  );

  // Calculate range information for accessibility
  const getPageRangeInfo = useMemo(() => {
    const startItem = currentRefinement * hitsPerPage + 1;
    const endItem = Math.min((currentRefinement + 1) * hitsPerPage, nbHits);
    return { startItem, endItem };
  }, [currentRefinement, nbHits, hitsPerPage]);

  // Don't render pagination if we can't refine or there's only one page
  if (!canRefine || nbPages <= 1) {
    return null;
  }

  return (
    <nav
      aria-label="Products pagination"
      className="tw-flex tw-w-full tw-flex-col tw-items-center tw-gap-4 tw-py-6"
      role="navigation"
    >
      {/* Results summary for better accessibility */}
      <div className="tw-text-center tw-text-sm tw-text-slate-gray">
        Showing {getPageRangeInfo.startItem} to {getPageRangeInfo.endItem} of{' '}
        {nbHits.toLocaleString()} results
      </div>

      {/* Pagination controls */}
      <ul className="tw-flex tw-list-none tw-items-center tw-gap-1 tw-pl-0">
        {/* Previous button */}
        <li>
          <DefaultButton
            onClick={() => handlePageChange(currentRefinement - 1)}
            onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) =>
              handleKeyDown(e, currentRefinement - 1)
            }
            disabled={isFirstPage}
            className="tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-md tw-text-slate-gray tw-transition-all tw-duration-200 hover:tw-bg-baby-blue hover:tw-text-main focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-main/20 focus:tw-ring-offset-2 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
            aria-label={`Go to previous page (page ${currentRefinement})`}
            type="button"
          >
            <ChevronLeft className="tw-h-4 tw-w-4" aria-hidden="true" />
          </DefaultButton>
        </li>

        {/* Page numbers */}
        {visiblePages.map((page, index) => {
          // Handle ellipsis
          if (page === 'ellipsis-start' || page === 'ellipsis-end') {
            return (
              <li key={`ellipsis-${index}`} className="tw-flex tw-items-center tw-justify-center">
                <span
                  className="tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-text-slate-gray"
                  aria-label="More pages"
                >
                  <MoreHorizontal className="tw-h-4 tw-w-4" aria-hidden="true" />
                </span>
              </li>
            );
          }

          // Regular page number
          const pageNumber = page as number;
          const isCurrentPage = pageNumber === currentRefinement;
          const displayPageNumber = pageNumber + 1; // Convert to 1-indexed for display

          return (
            <li key={pageNumber}>
              <DefaultButton
                onClick={() => handlePageChange(pageNumber)}
                onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) =>
                  handleKeyDown(e, pageNumber)
                }
                aria-current={isCurrentPage ? 'page' : undefined}
                aria-label={
                  isCurrentPage
                    ? `Current page, page ${displayPageNumber}`
                    : `Go to page ${displayPageNumber}`
                }
                type="button"
                className={`tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-md tw-text-sm tw-font-medium tw-ring-offset-2 tw-transition-all tw-duration-200 ${
                  isCurrentPage
                    ? 'tw-bg-main tw-text-white tw-shadow-sm'
                    : 'tw-text-darkblue hover:tw-bg-baby-blue hover:tw-text-main hover:tw-shadow-sm'
                } focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-main/20`}
              >
                {displayPageNumber}
              </DefaultButton>
            </li>
          );
        })}

        {/* Next button */}
        <li>
          <DefaultButton
            onClick={() => handlePageChange(currentRefinement + 1)}
            onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) =>
              handleKeyDown(e, currentRefinement + 1)
            }
            disabled={isLastPage}
            className="tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-md tw-text-slate-gray tw-transition-all tw-duration-200 hover:tw-bg-baby-blue hover:tw-text-main focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-main/20 focus:tw-ring-offset-2 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
            aria-label={`Go to next page (page ${currentRefinement + 2})`}
            type="button"
          >
            <ChevronRight className="tw-h-4 tw-w-4" aria-hidden="true" />
          </DefaultButton>
        </li>
      </ul>
    </nav>
  );
}
